// Semantic color "tones" — the single vocabulary every badge, pill, banner,
// dot and KPI tint in the app is styled from. Each tone maps to the five CSS
// variables defined in src/index.css (`--X`, `--X-foreground`, `--X-soft`,
// `--X-soft-foreground`, `--X-border`) via the `tone()` helper in
// tailwind.config.js, so every tone is legible in BOTH light and dark mode
// without any `dark:` variants at call sites.
//
// The maps below hold full literal class strings on purpose: Tailwind's JIT
// only generates classes it can see verbatim in source, so building
// `bg-${tone}-soft` at runtime would silently produce nothing.

export type Tone =
  | "brand"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "teal"
  | "magenta"
  | "orange"
  | "neutral";

/** Tinted background + matching text + outline — pills, chips, banners. */
export const TONE_SOFT: Record<Tone, string> = {
  brand: "bg-primary-soft text-primary-soft-foreground border-primary-border",
  success: "bg-success-soft text-success-soft-foreground border-success-border",
  warning: "bg-warning-soft text-warning-soft-foreground border-warning-border",
  danger: "bg-destructive-soft text-destructive-soft-foreground border-destructive-border",
  info: "bg-info-soft text-info-soft-foreground border-info-border",
  teal: "bg-teal-soft text-teal-soft-foreground border-teal-border",
  magenta: "bg-magenta-soft text-magenta-soft-foreground border-magenta-border",
  orange: "bg-orange-soft text-orange-soft-foreground border-orange-border",
  neutral: "bg-neutral-soft text-neutral-soft-foreground border-neutral-border",
};

/** Solid fill + text on it — primary actions, filled badges. */
export const TONE_SOLID: Record<Tone, string> = {
  brand: "bg-primary text-primary-foreground",
  success: "bg-success text-success-foreground",
  warning: "bg-warning text-warning-foreground",
  danger: "bg-destructive text-destructive-foreground",
  info: "bg-info text-info-foreground",
  teal: "bg-teal text-teal-foreground",
  magenta: "bg-magenta text-magenta-foreground",
  orange: "bg-orange text-orange-foreground",
  neutral: "bg-neutral text-neutral-foreground",
};

/** Just the solid fill — status dots, progress bars, column accents. */
export const TONE_DOT: Record<Tone, string> = {
  brand: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-destructive",
  info: "bg-info",
  teal: "bg-teal",
  magenta: "bg-magenta",
  orange: "bg-orange",
  neutral: "bg-neutral",
};

/** Standalone colored text on the page surface (metric numbers, icons).
 *  Success/teal/warning use their darker soft-foreground shade: the solid
 *  green/teal fills are ~2.7:1 on white, fine for dots and bars but not for
 *  text or icons. */
export const TONE_TEXT: Record<Tone, string> = {
  brand: "text-primary",
  success: "text-success-soft-foreground",
  warning: "text-warning-soft-foreground",
  danger: "text-destructive",
  info: "text-info",
  teal: "text-teal-soft-foreground",
  magenta: "text-magenta-soft-foreground",
  orange: "text-orange-soft-foreground",
  neutral: "text-muted-foreground",
};

/** Tinted background only — KPI tiles and soft row highlights. */
export const TONE_TINT: Record<Tone, string> = {
  brand: "bg-primary-soft border-primary-border",
  success: "bg-success-soft border-success-border",
  warning: "bg-warning-soft border-warning-border",
  danger: "bg-destructive-soft border-destructive-border",
  info: "bg-info-soft border-info-border",
  teal: "bg-teal-soft border-teal-border",
  magenta: "bg-magenta-soft border-magenta-border",
  orange: "bg-orange-soft border-orange-border",
  neutral: "bg-neutral-soft border-neutral-border",
};
